import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource, ridKey } from '../../../shared/model/keys'
import type { RefundCommand, RefundCommandKind, RefundCommandPhase, RefundCommandsState } from './types'

/**
 * Опасные команды возврата (создать, вернуть деньги, пробить чек, отменить),
 * исход которых клиент ещё ждёт. Срок — `session`: F5 и обрыв сети не теряют
 * `operationId` и не разблокируют «Вернуть», пока чтение кассы не покажет,
 * что случилось. Это не копия возврата: только ЧТО мы отправили.
 */
const initialState: RefundCommandsState = { pending: {} }

export interface RefundSeatRef {
  resourceId: string
  registerKey: string
}

/** Касса работы для запросов: ключ намерений и номер кассы кабинета (RP-C9). */
export interface RefundSeatWithDevice extends RefundSeatRef {
  deviceId: number
}

interface CommandRef extends RefundSeatRef {
  operationId: string
}

export const refundSeatKey = (ref: RefundSeatRef) => ridKey(ref.resourceId, ref.registerKey.toLowerCase())

function setPhase(state: RefundCommandsState, ref: CommandRef, phase: RefundCommandPhase): void {
  const entry = state.pending[refundSeatKey(ref)]
  if (entry !== undefined && entry.operationId === ref.operationId) entry.phase = phase
}

const refundCommandsSlice = createSlice({
  name: 'aqsi/refundCommands',
  initialState,
  reducers: {
    refundCommandStarted(state, action: PayloadAction<RefundCommand>) {
      state.pending[refundSeatKey(action.payload)] = action.payload
    },
    refundCommandUnknown(state, action: PayloadAction<CommandRef>) {
      setPhase(state, action.payload, 'unknown')
    },
    /** Окно отправки прошло, а касса так и не узнала команду: можно отдать новую (новый ключ). */
    refundCommandNotArrived(state, action: PayloadAction<CommandRef>) {
      setPhase(state, action.payload, 'not-arrived')
    },
    /** Итог известен: ответ пришёл, служба отказала до эффекта или чтение кассы показало результат. */
    refundCommandSettled(state, action: PayloadAction<CommandRef>) {
      const key = refundSeatKey(action.payload)
      if (state.pending[key]?.operationId === action.payload.operationId) delete state.pending[key]
    },
    refundCommandsRestored(state, action: PayloadAction<RefundCommandsState>) {
      state.pending = action.payload.pending
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.pending, action.payload.resourceId))
  },
})

export const {
  refundCommandStarted,
  refundCommandUnknown,
  refundCommandNotArrived,
  refundCommandSettled,
  refundCommandsRestored,
} = refundCommandsSlice.actions
export const refundCommandsReducer = refundCommandsSlice.reducer

export function refundCommandOf(state: RefundCommandsState, ref: RefundSeatRef): RefundCommand | undefined {
  return state.pending[refundSeatKey(ref)]
}

/** Команда ещё может изменить деньги, чек или занятость кассы: новую опасную команду на месте не отдаём. */
export function blocksRefundCommands(command: RefundCommand | undefined): boolean {
  return command !== undefined && command.phase !== 'not-arrived'
}

const KINDS: readonly RefundCommandKind[] = ['create', 'pay', 'fiscalize', 'cancel']
const PHASES: readonly RefundCommandPhase[] = ['sending', 'unknown', 'not-arrived']
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/** Номер чека кабинета — до 256 символов, без управляющих (как проверяет служба). */
function isReceiptId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 1 &&
    value.length <= 256 &&
    [...value].every((char) => {
      const code = char.charCodeAt(0)
      return code >= 0x20 && code !== 0x7f
    })
  )
}

/**
 * Проверка сохранённого (D-072). Команда, бывшая «в полёте» на момент F5,
 * читается как `unknown`: ответ потерян вместе со страницей, исход узнаётся
 * чтением кассы, а не повтором.
 */
export function decodeRefundCommands(raw: unknown): RefundCommandsState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const pending = (raw as { pending?: unknown }).pending
  if (typeof pending !== 'object' || pending === null) return undefined
  const result: RefundCommandsState = { pending: {} }
  for (const value of Object.values(pending as Record<string, unknown>).slice(0, 50)) {
    if (typeof value !== 'object' || value === null) continue
    const entry = value as Record<string, unknown>
    if (
      typeof entry.resourceId !== 'string' ||
      !UUID.test(entry.resourceId) ||
      typeof entry.registerKey !== 'string' ||
      !UUID.test(entry.registerKey) ||
      typeof entry.deviceId !== 'number' ||
      !Number.isSafeInteger(entry.deviceId) ||
      entry.deviceId <= 0 ||
      !isReceiptId(entry.receiptId) ||
      !(entry.refundId === null || (typeof entry.refundId === 'string' && UUID.test(entry.refundId))) ||
      typeof entry.operationId !== 'string' ||
      !UUID.test(entry.operationId) ||
      !KINDS.includes(entry.kind as RefundCommandKind) ||
      typeof entry.version !== 'number' ||
      !Number.isSafeInteger(entry.version) ||
      typeof entry.startedAt !== 'string' ||
      !PHASES.includes(entry.phase as RefundCommandPhase)
    ) {
      continue
    }
    const kind = entry.kind as RefundCommandKind
    // Команда над возвратом без возврата (или создание с возвратом) — запись чужой формы.
    if ((kind === 'create') !== (entry.refundId === null)) continue
    const command: RefundCommand = {
      resourceId: entry.resourceId,
      registerKey: entry.registerKey,
      deviceId: entry.deviceId,
      kind,
      receiptId: entry.receiptId,
      refundId: entry.refundId,
      version: entry.version,
      operationId: entry.operationId,
      startedAt: entry.startedAt,
      phase: entry.phase === 'sending' ? 'unknown' : (entry.phase as RefundCommandPhase),
    }
    result.pending[refundSeatKey(command)] = command
  }
  return result
}
